import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Home, PlusCircle, MessageCircle, Bell, User, HeartHandshake, LogOut, Shield, Gift, MailCheck, Globe2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import ForcePasswordChange from "@/components/ForcePasswordChange";

const NAV = [
  { to: "/app", icon: Home, label: "Home", end: true },
  { to: "/app/communities", icon: Globe2, label: "Towns" },
  { to: "/app/new", icon: PlusCircle, label: "Post" },
  { to: "/app/messages", icon: MessageCircle, label: "Messages", key: "messages" },
  { to: "/app/profile", icon: User, label: "Profile" },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [unreadMsg, setUnreadMsg] = useState(0);

  const loadBadges = async () => {
    try {
      const [n, c] = await Promise.all([api.get("/notifications"), api.get("/connections")]);
      setUnread(n.data.unread || 0);
      setUnreadMsg(c.data.reduce((s, x) => s + (x.unread || 0), 0));
    } catch {}
  };

  useEffect(() => {
    loadBadges();
    const t = setInterval(loadBadges, 15000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const isActive = (to, end) => (end ? location.pathname === to : location.pathname.startsWith(to));
  const badgeFor = (key) => (key === "messages" ? unreadMsg : key === "alerts" ? unread : 0);

  const navItems = NAV;

  const handleLogout = async () => { await logout(); navigate("/"); };

  const resendVerify = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent — check your inbox"); }
    catch { toast.error("Could not send email. Please try again."); }
  };

  if (user && user.must_change_password) return <ForcePasswordChange />;

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Top nav (desktop) */}
      <header className="sticky top-0 z-40 bg-background border-b-2 border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-2">
          <Link to="/app" className="flex items-center gap-2.5 mr-4" data-testid="nav-logo">
            <div className="w-11 h-11 rounded-xl bg-terracotta flex items-center justify-center">
              <HeartHandshake className="text-white" size={26} strokeWidth={2.5} />
            </div>
            <span className="font-head font-extrabold text-2xl text-forest-900 hidden sm:block">Connect</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-2">
            {navItems.map((n) => {
              const active = isActive(n.to, n.end);
              const badge = badgeFor(n.key);
              return (
                <button
                  key={n.to}
                  onClick={() => navigate(n.to)}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold transition-colors ${active ? "bg-terracotta text-white" : "text-forest-700 hover:bg-terracotta/10"}`}
                >
                  <n.icon size={22} strokeWidth={2.5} />
                  {n.label}
                  {badge > 0 && <span className="absolute -top-1 -right-1 bg-gold text-forest-900 text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">{badge}</span>}
                </button>
              );
            })}
            {user?.role === "admin" && (
              <button onClick={() => navigate("/app/admin")} data-testid="nav-admin"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold transition-colors ${isActive("/app/admin") ? "bg-forest-900 text-white" : "text-forest-700 hover:bg-forest-900/10"}`}>
                <Shield size={22} strokeWidth={2.5} /> Admin
              </button>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button onClick={() => navigate("/app/notifications")} data-testid="nav-alerts-bell"
              className="relative w-11 h-11 rounded-full hover:bg-terracotta/10 flex items-center justify-center text-forest-700">
              <Bell size={24} strokeWidth={2.5} />
              {unread > 0 && <span className="absolute top-0.5 right-0.5 bg-terracotta text-white text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">{unread}</span>}
            </button>
            <button onClick={() => navigate("/app/profile")} className="flex items-center gap-2" data-testid="nav-profile-avatar">
              <Avatar user={user} size={40} />
            </button>
            <button onClick={handleLogout} data-testid="logout-btn"
              className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-forest-700 hover:bg-terracotta/10 transition-colors">
              <LogOut size={20} strokeWidth={2.5} /> <span className="hidden lg:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      {user && user.verified === false && (
        <div className="bg-gold-light border-b-2 border-gold" data-testid="verify-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
            <MailCheck size={22} strokeWidth={2.5} className="text-forest-900 shrink-0" />
            <span className="text-forest-900 font-semibold">Please verify your email to build trust with the community.</span>
            <button onClick={resendVerify} data-testid="resend-verify"
              className="ml-auto min-h-[44px] px-5 rounded-full bg-forest-900 text-white font-semibold hover:bg-forest-700 transition-colors">Resend email</button>
          </div>
        </div>
      )}

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-10">
        <Outlet />
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background border-t-2 border-border">
        <div className="flex items-stretch justify-around">
          {navItems.map((n) => {
            const active = isActive(n.to, n.end);
            const badge = badgeFor(n.key);
            return (
              <button key={n.to} onClick={() => navigate(n.to)} data-testid={`bottomnav-${n.label.toLowerCase()}`}
                className={`relative flex-1 flex flex-col items-center gap-0.5 py-2.5 ${active ? "text-terracotta" : "text-forest-500"}`}>
                <n.icon size={26} strokeWidth={2.5} />
                <span className="text-xs font-semibold">{n.label}</span>
                {badge > 0 && <span className="absolute top-1 right-1/4 bg-gold text-forest-900 text-[10px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{badge}</span>}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
